import { z } from 'zod';
import { NeedEffects, PlayReply } from './play';

// The airport, as Lagos Life's: London City Airport is a place on the map with a check-in desk, a departure
// lounge, duty free, a food court, a viewing deck and airport jobs. "Book a flight" at check-in opens the
// flights desk: the cities the game's own airline will fly to, in four cabins, at a fare that rises at peak
// hours. Until another city is built every destination "opens soon" (as Lagos Life's locked cities do); the
// booking itself comes with the first city. Everything here is content the admins edit.

const id = z.string().regex(/^[a-z0-9_]{2,30}$/);
const icon = z.string().trim().min(1).max(16);
const effects = NeedEffects;

export const CABIN_KEYS = ['economy', 'premium', 'business', 'first'] as const;
export type CabinKey = (typeof CABIN_KEYS)[number];
export const Cabin = z.object({
  label: z.string().trim().min(2).max(30),
  icon,
  // the fare compared with economy
  times: z.number().min(1).max(50),
  perk: z.string().trim().max(120),
  seat: z.string().trim().max(40),
  // what the flight does to the needs in this cabin (content made before uses the default cabin's)
  effects: effects.optional(),
});
export type Cabin = z.infer<typeof Cabin>;

// somewhere the airline flies: another city of the game
export const Destination = z.object({
  id,
  name: z.string().trim().min(2).max(40),
  icon,
  airport: z.string().trim().min(2).max(60),
  // IATA-style code shown on the board
  code: z
    .string()
    .trim()
    .regex(/^[A-Z]{3}$/),
  // an economy seat, in pence
  farePence: z.number().int().min(100).max(100_000_000),
  // how long the flight takes, in real seconds (the ride on the screen)
  flightSeconds: z.number().int().min(5).max(600),
  // shown on the board (a route can be taken off it); every destination "opens soon" until its city is
  // built, when flights get their booking
  visible: z.boolean(),
});
export type Destination = z.infer<typeof Destination>;

// one of the game's own airports (a place on the map with a terminal): flights go between them. The fare is an
// economy seat to fly there, the flight how long the ride takes on the screen.
export const AirportStop = z.object({
  placeId: id,
  code: z
    .string()
    .trim()
    .regex(/^[A-Z]{3}$/),
  name: z.string().trim().min(2).max(40),
  farePence: z.number().int().min(100).max(100_000_000),
  flightSeconds: z.number().int().min(10).max(300),
  // how many gates it has (the gate on the boarding pass)
  gates: z.number().int().min(1).max(40),
  visible: z.boolean(),
});
export type AirportStop = z.infer<typeof AirportStop>;
// what happens on board, line by line, as Lagos Life's flights tell it: `at` is how far through the flight
// (0 take-off, 1 landing)
export const FlightScript = z.object({
  boarding: z.string().trim().min(1).max(120),
  takeOff: z.string().trim().min(1).max(120),
  lines: z.array(z.object({ at: z.number().min(0).max(1), text: z.string().trim().min(1).max(120) })).max(12),
  // {airport} is the airport landed at
  landed: z.string().trim().min(1).max(120),
});
export type FlightScript = z.infer<typeof FlightScript>;
// cover for the bags: costs this much, and a bag goes missing this often (covered: nothing lost; not
// covered: what losing it does to the needs)
export const BagCover = z.object({
  pence: z.number().int().min(0).max(1_000_000),
  lossPercent: z.number().int().min(0).max(50),
  lostEffects: effects,
});
export type BagCover = z.infer<typeof BagCover>;

// your own jet (as Lagos Life's "Fly your own jet"): bought in three sizes, parked on the private apron, flown
// to any of the game's airports with no boarding, paying only its fuel; sold back for part of its price
export const JET_KINDS = ['light', 'mid', 'heavy'] as const;
export type JetKind = (typeof JET_KINDS)[number];
const JetModel = z.object({
  label: z.string().trim().min(2).max(30),
  icon,
  pricePence: z.number().int().min(100).max(100_000_000_000),
  fuelPence: z.number().int().min(1).max(100_000_000),
  blurb: z.string().trim().max(120),
});
export const PrivateJets = z.object({
  seconds: z.number().int().min(5).max(120),
  // what selling it back gives, of what it cost
  sellShare: z.number().min(0).max(1),
  // the paint on the players' jets (their name goes on the side)
  colours: z.object({ body: z.string(), tail: z.string(), stripe: z.string() }),
  kinds: z.object({ light: JetModel, mid: JetModel, heavy: JetModel }),
});
export type PrivateJets = z.infer<typeof PrivateJets>;
// a chopper from an airport's helipad straight to the door of any place on the map
export const ChopperContent = z.object({
  farePence: z.number().int().min(100).max(100_000_000),
  seconds: z.number().int().min(5).max(120),
  visible: z.boolean(),
  // what the card says: lifting off, over London, coming down ({place} is where it lands)
  lines: z.object({
    lift: z.string().trim().min(1).max(120),
    over: z.string().trim().min(1).max(120),
    down: z.string().trim().min(1).max(120),
  }),
});
export type ChopperContent = z.infer<typeof ChopperContent>;

export const AirportContent = z.object({
  airline: z.object({
    name: z.string().trim().min(2).max(40),
    code: z
      .string()
      .trim()
      .regex(/^[A-Z0-9]{2}$/),
    // the livery: body, tail and stripe
    colours: z.object({ body: z.string(), tail: z.string(), stripe: z.string() }),
    // the jets' names, painted by the door
    fleet: z.array(z.string().trim().min(1).max(40)).min(1).max(12),
  }),
  cabins: z.object({ economy: Cabin, premium: Cabin, business: Cabin, first: Cabin }),
  destinations: z.array(Destination).max(20),
  // fares rise at busy times: these hours (London time, from–to) by this much
  peak: z.object({
    hours: z.array(z.tuple([z.number().int().min(0).max(24), z.number().int().min(0).max(24)])).max(4),
    times: z.number().min(1).max(5),
  }),
  // from the gate to the air, in real seconds
  boardingSeconds: z.number().int().min(0).max(300),
  // what players are told while no city is open
  soonNote: z.string().trim().max(160),
  // the game's own airports, flown between (content made before uses the default list)
  airports: z.array(AirportStop).max(10).optional(),
  script: FlightScript.optional(),
  bagCover: BagCover.optional(),
  // "Bring someone along" (as Lagos Life's): how many friends a flyer can offer seats to, and how long the
  // flight waits at the gate for them to hop in
  bringAlong: z
    .object({ seats: z.number().int().min(1).max(8), holdSeconds: z.number().int().min(10).max(600) })
    .optional(),
  privateJets: PrivateJets.optional(),
  chopper: ChopperContent.optional(),
});
export type AirportContent = z.infer<typeof AirportContent>;

export const DEFAULT_AIRPORT: AirportContent = {
  airline: {
    name: 'London Life Air',
    code: 'LL',
    colours: { body: '#f4f6f8', tail: '#4c1d95', stripe: '#f6b83d' },
    fleet: ['Spirit of Docklands', 'Pearly Queen', 'Thames Tide', 'Pride of Poplar', 'Greenwich Meridian'],
  },
  cabins: {
    economy: {
      label: 'Economy',
      icon: '💺',
      times: 1,
      perk: 'Your seat, a drink and a snack',
      seat: '3-3 rows',
      effects: { energy: -6, fun: 6 },
    },
    premium: {
      label: 'Premium Economy',
      icon: '🛋️',
      times: 1.8,
      perk: 'A wider seat, more legroom, a hot meal',
      seat: '2-2, more legroom',
      effects: { fun: 10, hunger: 12 },
    },
    business: {
      label: 'Business',
      icon: '🥂',
      times: 4,
      perk: 'Lounge access, a lie-flat seat, bubbly and a proper meal',
      seat: 'Lie-flat seats',
      effects: { energy: 10, fun: 16, hunger: 30 },
    },
    first: {
      label: 'First Class',
      icon: '👑',
      times: 9,
      perk: "Your own suite, the chef's menu, a car to the plane",
      seat: 'Private suites',
      effects: { energy: 20, fun: 24, hunger: 40 },
    },
  },
  destinations: [
    {
      id: 'manchester',
      name: 'Manchester',
      icon: '🐝',
      airport: 'Manchester Airport',
      code: 'MAN',
      farePence: 9_900,
      flightSeconds: 32,
      visible: true,
    },
    {
      id: 'birmingham',
      name: 'Birmingham',
      icon: '🏭',
      airport: 'Birmingham Airport',
      code: 'BHX',
      farePence: 7_900,
      flightSeconds: 28,
      visible: true,
    },
  ],
  peak: {
    hours: [
      [6, 9],
      [16, 20],
    ],
    times: 1.5,
  },
  boardingSeconds: 20,
  soonNote: 'Manchester and Birmingham open soon. Until then, fly between London City, Heathrow and Gatwick.',
  airports: [
    {
      placeId: 'heathrow',
      code: 'LHR',
      name: 'Heathrow',
      farePence: 6_900,
      flightSeconds: 32,
      gates: 5,
      visible: true,
    },
    {
      placeId: 'gatwick',
      code: 'LGW',
      name: 'Gatwick',
      farePence: 5_900,
      flightSeconds: 30,
      gates: 5,
      visible: true,
    },
    {
      placeId: 'airport',
      code: 'LCY',
      name: 'London City',
      farePence: 4_900,
      flightSeconds: 26,
      gates: 4,
      visible: true,
    },
  ],
  script: {
    boarding: '🧳 Boarding at the gate: find your seat, bags in the overhead lockers',
    takeOff: '🛫 Cabin crew, seats for take-off. Here we go!',
    lines: [
      { at: 0.06, text: '⬆️ Climbing out over London…' },
      { at: 0.2, text: '☁️ Through the clouds. The seatbelt sign is off' },
      { at: 0.32, text: "☕ Tea or coffee? Here's your biscuit" },
      { at: 0.5, text: '🌍 Cruising at 35,000 ft' },
      { at: 0.68, text: '📰 The man beside you is doing the crossword out loud' },
      { at: 0.84, text: "⬇️ Seatbelts on, we're starting our descent" },
    ],
    landed: '🛬 Touchdown! Welcome to {airport}',
  },
  bagCover: { pence: 725, lossPercent: 8, lostEffects: { fun: -18 } },
  bringAlong: { seats: 3, holdSeconds: 120 },
  privateJets: {
    seconds: 16,
    sellShare: 0.6,
    colours: { body: '#1d2433', tail: '#c9a227', stripe: '#c9a227' },
    kinds: {
      light: {
        label: 'Light jet',
        icon: '🛩️',
        pricePence: 25_000_000,
        fuelPence: 150_000,
        blurb: 'Six seats, a small galley, and London in a quarter of an hour',
      },
      mid: {
        label: 'Mid-size jet',
        icon: '✈️',
        pricePence: 60_000_000,
        fuelPence: 250_000,
        blurb: 'Eight leather seats, a bar and room to stand up',
      },
      heavy: {
        label: 'Heavy jet',
        icon: '🛫',
        pricePence: 150_000_000,
        fuelPence: 450_000,
        blurb: 'A bedroom, a shower and a crew of three',
      },
    },
  },
  chopper: {
    farePence: 45_000,
    seconds: 14,
    visible: true,
    lines: {
      lift: '🚁 Lifting off the helipad',
      over: '🏙️ Low over London, the Thames below',
      down: '⬇️ Coming down at {place}',
    },
  },
};

// is it a peak hour in London?
export function isPeakHour(content: AirportContent, hourInLondon: number) {
  return content.peak.hours.some(([from, to]) => hourInLondon >= from && hourInLondon < to);
}
// what a seat costs: the economy fare, times the cabin, times peak when it is one; to the penny
export function fareOf(
  content: AirportContent,
  destination: { farePence: number },
  cabin: CabinKey,
  hourInLondon: number,
) {
  const peak = isPeakHour(content, hourInLondon) ? content.peak.times : 1;
  return Math.round(destination.farePence * content.cabins[cabin].times * peak);
}
// the flight's number, the same every time for a route (FNV hash of it)
export function flightNumber(content: AirportContent, destinationId: string) {
  let hash = 0x811c9dc5;
  for (const char of `london|${destinationId}`) hash = Math.imul(hash ^ char.charCodeAt(0), 0x01000193);
  return `${content.airline.code} ${100 + ((hash >>> 0) % 800)}`;
}
// the jet on the route, from the fleet
export function jetOf(content: AirportContent, destinationId: string) {
  let hash = 0;
  for (const char of destinationId) hash = (hash * 31 + char.charCodeAt(0)) >>> 0;
  return content.airline.fleet[hash % content.airline.fleet.length]!;
}
export const hourInLondon = (now = new Date()) =>
  Number(
    new Intl.DateTimeFormat('en-GB', { hour: 'numeric', hourCycle: 'h23', timeZone: 'Europe/London' }).format(now),
  );

// the game's airports, the script and the bag cover (content made before these existed uses the defaults)
export const airportsOf = (content: AirportContent) => content.airports ?? DEFAULT_AIRPORT.airports!;
export const scriptOf = (content: AirportContent) => content.script ?? DEFAULT_AIRPORT.script!;
export const bagCoverOf = (content: AirportContent) => content.bagCover ?? DEFAULT_AIRPORT.bagCover!;
export const bringAlongOf = (content: AirportContent) => content.bringAlong ?? DEFAULT_AIRPORT.bringAlong!;
export const privateJetsOf = (content: AirportContent) => content.privateJets ?? DEFAULT_AIRPORT.privateJets!;
export const chopperOf = (content: AirportContent) => content.chopper ?? DEFAULT_AIRPORT.chopper!;
// what a jet sells back for
export const jetSellPence = (content: AirportContent, pricePaid: number) =>
  Math.floor(pricePaid * privateJetsOf(content).sellShare);
export const cabinEffectsOf = (content: AirportContent, cabin: CabinKey) =>
  content.cabins[cabin].effects ?? DEFAULT_AIRPORT.cabins[cabin].effects ?? {};
// a stable number from text (FNV-1a)
const hashOf = (text: string) => {
  let hash = 0x811c9dc5;
  for (const char of text) hash = Math.imul(hash ^ char.charCodeAt(0), 0x01000193);
  return hash >>> 0;
};
// a route's flight number, the same every time (LL 452)
export const routeNumber = (content: AirportContent, from: string, to: string) =>
  `${content.airline.code} ${100 + (hashOf(`${from}>${to}`) % 800)}`;
// the jet flying a flight: its name from the fleet and its registration (G-LLAB)
export function jetFor(content: AirportContent, key: string) {
  const hash = hashOf(key);
  const fleet = content.airline.fleet;
  const letters = 'ABCDEFGHJKLMNPRSTUVWXYZ';
  const index = hash % fleet.length;
  return {
    name: fleet[index]!,
    registration: `G-${content.airline.code.replace(/[^A-Z]/g, 'L').padEnd(2, 'L')}${letters[index % letters.length]}${letters[(hash >>> 8) % letters.length]}`,
  };
}
export const gateFor = (stop: Pick<AirportStop, 'gates'>, key: string) => 1 + (hashOf(`gate:${key}`) % stop.gates);
// is a bag lost on this flight? (decided once from the flight, the same however often it is asked)
export const bagLost = (cover: BagCover, flightId: string) => hashOf(`bag:${flightId}`) % 100 < cover.lossPercent;

// problems that stop the content being published
export function airportProblems(content: AirportContent): string[] {
  const problems: string[] = [];
  const ids = new Set<string>();
  for (const destination of content.destinations) {
    if (ids.has(destination.id)) problems.push(`Two destinations have the id "${destination.id}".`);
    ids.add(destination.id);
  }
  for (const [from, to] of content.peak.hours)
    if (from >= to) problems.push(`A peak time ends (${to}) before it starts (${from}).`);
  if (content.cabins.economy.times !== 1) problems.push('Economy is the base fare: its multiplier must be 1.');
  const stops = new Set<string>();
  for (const stop of content.airports ?? []) {
    if (stops.has(stop.placeId)) problems.push(`Two airports are the place "${stop.placeId}".`);
    stops.add(stop.placeId);
  }
  return problems;
}

// ---------- flights between the game's airports ----------
// how: a seat on the airline, your own jet, a chopper (to any place on the map, not only an airport), or a
// train from King's Cross (contracts rail.ts; its trips are kept with the flights)
export const FLIGHT_MODES = ['air', 'jet', 'chopper', 'train'] as const;
export type FlightMode = (typeof FLIGHT_MODES)[number];
export const FlyRequest = z.object({
  // the airport flown from (a cab takes the player there first if they are elsewhere) and to
  from: id,
  to: id,
  cabin: z.enum(CABIN_KEYS),
  // cover for the bags
  covered: z.boolean(),
  // a seat on the airline when not given (trains are booked at the station: TrainRequest)
  mode: z.enum(['air', 'jet', 'chopper']).optional(),
});
export type FlyRequest = z.infer<typeof FlyRequest>;
const Stop = z.object({ placeId: z.string(), code: z.string(), name: z.string() });
export const FlightView = z.object({
  id: z.string(),
  mode: z.enum(FLIGHT_MODES),
  number: z.string(),
  registration: z.string(),
  jetName: z.string(),
  cabin: z.enum(CABIN_KEYS),
  from: Stop,
  to: Stop,
  gate: z.number().int(),
  farePence: z.number().int(),
  coverPence: z.number().int(),
  // the cab to the airport ends, boarding ends (take-off), the jet lands
  bookedAt: z.string(),
  boardsAt: z.string(),
  takesOffAt: z.string(),
  landsAt: z.string(),
  // once landed and settled: was a bag lost (covered or not)
  bagLost: z.boolean().nullable(),
  // "Bring someone along": on someone else's flight (their name), the others on board with you, and seats
  // offered still waiting for an answer
  host: z.string().nullable(),
  crew: z.array(z.string()),
  waiting: z.number().int(),
  // a player's company's train: its paint and the company (for the rider's stars)
  paint: z.object({ body: z.string(), stripe: z.string() }).nullable().optional(),
  companyId: z.string().uuid().nullable().optional(),
});
export type FlightView = z.infer<typeof FlightView>;
// offering seats on my flight to people I know (they buy their own ticket)
export const OfferSeatsRequest = z.object({ lifeIds: z.array(z.string().uuid()).min(1).max(8) });
export type OfferSeatsRequest = z.infer<typeof OfferSeatsRequest>;
// a seat someone saved me on their flight
export const SeatOffer = z.object({
  id: z.string().uuid(),
  host: z.string(),
  hostLifeId: z.string().uuid(),
  number: z.string(),
  cabin: z.enum(CABIN_KEYS),
  from: Stop,
  to: Stop,
  // what my ticket costs now, and when the jet leaves without me
  farePence: z.number().int(),
  expiresAt: z.string(),
});
export type SeatOffer = z.infer<typeof SeatOffer>;
export const SeatOffersReply = z.object({ offers: z.array(SeatOffer) });
export type SeatOffersReply = z.infer<typeof SeatOffersReply>;
export const FlightReply = z.object({ flight: FlightView.nullable() });
export type FlightReply = z.infer<typeof FlightReply>;
// booking a flight: where things stand now (on the way, or boarding) and the flight
export const FlyReply = PlayReply.extend({ flight: FlightView });
export type FlyReply = z.infer<typeof FlyReply>;

// ---------- your own jet ----------
export const MyJet = z.object({
  id: z.string().uuid(),
  kind: z.enum(JET_KINDS),
  label: z.string(),
  // painted on it: the owner's name
  name: z.string(),
  registration: z.string(),
  pricePence: z.number().int(),
  fuelPence: z.number().int(),
  sellPence: z.number().int(),
  boughtAt: z.string(),
});
export type MyJet = z.infer<typeof MyJet>;
export const MyJetReply = z.object({
  jet: MyJet.nullable(),
  walletPence: z.number().int(),
  message: z.string().optional(),
});
export type MyJetReply = z.infer<typeof MyJetReply>;
export const BuyJetRequest = z.object({ kind: z.enum(JET_KINDS) });
export type BuyJetRequest = z.infer<typeof BuyJetRequest>;
